import React, { Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { PageHeader } from '@/components/ui/page-header';
import NetworkingHub from '@/components/networking/NetworkingHub';
import UserNetworkingStats from '@/components/networking/UserNetworkingStats';
import ProfileCompletionBanner from '@/components/networking/ProfileCompletionBanner';
import ProfileCompletionChecker from '@/components/profile/ProfileCompletionChecker';
const RecommendedMembers = React.lazy(() => import('@/components/networking/RecommendedMembers'));
const MemberProfileSuggestions = React.lazy(() => import('@/components/networking/MemberProfileSuggestions'));
const DiscoveryHub = React.lazy(() => import('@/components/networking/DiscoveryHub'));
const PersonalNetworkingCRM = React.lazy(() => import('@/components/networking/PersonalNetworkingCRM'));
import NetworkingAIAssistant from '@/components/networking/NetworkingAIAssistant';
import RelatedOpportunities from '@/components/networking/RelatedOpportunities';
import { useAuth } from '@/contexts/AuthContext';
import PullToRefresh from '@/components/mobile/PullToRefresh';
import FloatingActionButton from '@/components/mobile/FloatingActionButton';
import { TrendingSkillsWidget } from '@/components/networking/TrendingSkillsWidget';
import { TravelingMembersWidget } from '@/components/networking/TravelingMembersWidget';

import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Brain, MessageSquare } from 'lucide-react';
import { NetworkingNotificationBanner } from '@/components/networking/NetworkingNotificationBanner';
import { NetworkingProvider, useNetworking } from '@/contexts/NetworkingContext';

const NetworkingContent = () => {
  const { user, member, isLoading: authLoading } = useAuth();
  const isMobile = useIsMobile();
  const { networkingStats } = useNetworking();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'hub';
  const [activeTab, setActiveTab] = React.useState(initialTab);
  const [showStats, setShowStats] = React.useState(false);

  const handleRefresh = async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    window.location.reload();
  };

  React.useEffect(() => {
    const timer = setTimeout(() => setShowStats(true), 800);
    return () => clearTimeout(timer);
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Vérification des permissions...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className={cn("min-h-screen bg-background", isMobile && "pb-20")}>
      <NavBar />
      
      {!isMobile && (
        <PageHeader 
          title="Networking"
          description="Échangez, collaborez et développez votre réseau professionnel"
        />
      )}
      
      <main>
        <PullToRefresh onRefresh={handleRefresh}>
          <section className={cn("py-12", isMobile && "py-6")}>
            <div className="node-container space-y-8">
              {isMobile && (
                <div className="text-center mb-6">
                  <h1 className="text-2xl font-bold text-foreground">Networking</h1>
                  <p className="text-muted-foreground text-sm">
                    Échangez et développez votre réseau
                  </p>
                </div>
              )}

              <Suspense fallback={<div className="h-16 animate-pulse bg-muted/50 rounded-lg" />}>
                <ProfileCompletionChecker />
              </Suspense>
              
              {member && (
                <Suspense fallback={<div className="h-12 animate-pulse bg-muted/50 rounded-lg" />}>
                  <ProfileCompletionBanner member={member} />
                </Suspense>
              )}

              {showStats && (
                <Suspense fallback={<div className="h-14 animate-pulse bg-muted/50 rounded-lg" />}>
                  <UserNetworkingStats />
                </Suspense>
              )}

              <Suspense fallback={<div className="h-48 animate-pulse bg-muted/50 rounded-lg" />}>
                <PersonalNetworkingCRM />
              </Suspense>

              <Suspense fallback={<div className="h-64 animate-pulse bg-muted/50 rounded-lg" />}>
                <DiscoveryHub />
              </Suspense>
              
              <Suspense fallback={<div className="h-16 animate-pulse bg-muted/50 rounded-lg" />}>
                <NetworkingNotificationBanner
                  unreadMessages={networkingStats?.unreadMessages || 0}
                  pendingRequests={networkingStats?.pendingRequests || 0}
                  onNavigateToMessages={() => setActiveTab('hub')}
                  onNavigateToRequests={() => setActiveTab('hub')}
                />
              </Suspense>
              
              {/* 2 onglets : Messages et Suggestions IA */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="hub" className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    <span className={cn(isMobile && "text-xs")}>Messages</span>
                  </TabsTrigger>
                  <TabsTrigger value="ai-assistant" className="flex items-center gap-2">
                    <Brain className="h-4 w-4" />
                    <span className={cn(isMobile && "text-xs")}>Suggestions IA</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="hub" className="mt-6">
                  <div className={cn(
                    "grid gap-8",
                    isMobile ? "grid-cols-1" : "lg:grid-cols-3"
                  )}>
                    <div className={cn(isMobile ? "order-2" : "lg:col-span-2")}>
                      <NetworkingHub />
                    </div>
                    <div className={cn(isMobile ? "order-1" : "lg:col-span-1", "space-y-6")}>
                      <TrendingSkillsWidget />
                      <RelatedOpportunities member={member} />
                      <TravelingMembersWidget />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="ai-assistant" className="mt-6">
                  <div className="space-y-8">
                    <NetworkingAIAssistant />
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          Membres recommandés
                        </span>
                      </div>
                    </div>
                    
                    <Suspense fallback={<div className="h-32 animate-pulse bg-muted/50 rounded-lg" />}>
                      <MemberProfileSuggestions />
                    </Suspense>
                    
                    <Suspense fallback={
                      <div className="p-6 text-center">
                        <div className="animate-pulse">Chargement des recommandations IA...</div>
                      </div>
                    }>
                      <RecommendedMembers />
                    </Suspense>
                  </div>
                </TabsContent>
              </Tabs>

            </div>
          </section>
        </PullToRefresh>
      </main>

      {!isMobile && <Footer />}
      
      {isMobile && (
        <FloatingActionButton
          actions={[
            {
              icon: () => <span className="text-white">💬</span>,
              label: 'Message',
              action: () => setActiveTab('hub'),
              color: 'bg-blue-500'
            }
          ]}
        />
      )}
    </div>
  );
};

const Networking = () => {
  return (
    <NetworkingProvider>
      <NetworkingContent />
    </NetworkingProvider>
  );
};

export default Networking;
